import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Tables, TablesInsert, TablesUpdate } from '@/integrations/supabase/types';
import { batchUpdateManager } from '@/utils/realtimeOptimization';

type HotelService = Tables<'hotel_services'>;
type GuestServiceOrder = Tables<'guest_service_orders'>;
type GuestServiceOrderInsert = TablesInsert<'guest_service_orders'>;
type GuestServiceOrderUpdate = TablesUpdate<'guest_service_orders'>;

export const useGuestServices = (guestAccessId?: string, hotelId?: string) => {
  const [services, setServices] = useState<HotelService[]>([]);
  const [orders, setOrders] = useState<GuestServiceOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchServices = async (page = 0, pageSize = 50) => {
    try {
      const start = page * pageSize;
      const end = start + pageSize - 1;

      // Server-side пагинация
      let query = supabase
        .from('hotel_services')
        .select('*')
        .eq('is_active', true);

      if (hotelId) {
        query = query.eq('hotel_id', hotelId);
      }

      const { data, error } = await query
        .range(start, end)
        .order('category', { ascending: true });

      if (error) throw error;
      setServices(data || []);
    } catch (error: any) {
      console.error('Error fetching services:', error);
      toast({
        title: "Ошибка",
        description: "Не удалось загрузить услуги",
        variant: "destructive",
      });
    }
  };

  const fetchOrders = async () => {
    if (!guestAccessId) return;

    try {
      const { data, error } = await supabase
        .from('guest_service_orders')
        .select(`
          *,
          service:hotel_services(*)
        `)
        .eq('guest_access_id', guestAccessId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setOrders(data || []);
    } catch (error: any) {
      console.error('Error fetching orders:', error);
      toast({
        title: "Ошибка",
        description: "Не удалось загрузить ваши заказы",
        variant: "destructive",
      });
    }
  };

  const createOrder = async (order: Omit<GuestServiceOrderInsert, 'guest_access_id'>) => {
    if (!guestAccessId) throw new Error('Guest access ID required');

    try {
      const { data, error } = await supabase
        .from('guest_service_orders')
        .insert({ ...order, guest_access_id: guestAccessId })
        .select(`
          *,
          service:hotel_services(*)
        `)
        .single();

      if (error) throw error;

      setOrders(prev => [data, ...prev]);
      toast({
        title: "Заказ оформлен",
        description: "Ваш заказ принят к обработке",
      });

      return data;
    } catch (error: any) {
      console.error('Error creating order:', error);
      toast({
        title: "Ошибка",
        description: error.message || "Не удалось оформить заказ",
        variant: "destructive",
      });
      throw error;
    }
  };

  const updateOrder = async (orderId: string, updates: GuestServiceOrderUpdate) => {
    try {
      const { data, error } = await supabase
        .from('guest_service_orders')
        .update(updates)
        .eq('id', orderId)
        .select(`
          *,
          service:hotel_services(*)
        `)
        .single();

      if (error) throw error;

      setOrders(prev => prev.map(order => 
        order.id === orderId ? data : order
      ));

      toast({
        title: "Заказ обновлен",
        description: "Изменения сохранены",
      });

      return data;
    } catch (error: any) {
      console.error('Error updating order:', error);
      toast({
        title: "Ошибка",
        description: error.message || "Не удалось обновить заказ",
        variant: "destructive",
      });
      throw error;
    }
  };

  const cancelOrder = async (orderId: string) => {
    return updateOrder(orderId, { status: 'cancelled' });
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchServices(),
        fetchOrders()
      ]);
      setLoading(false);
    };

    loadData();

    // Subscribe to real-time updates for orders with batching
    if (guestAccessId) {
      const channel = supabase
        .channel('guest-orders')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'guest_service_orders',
            filter: `guest_access_id=eq.${guestAccessId}`
          },
          (payload) => {
            batchUpdateManager.addUpdate(
              `guest_orders_${guestAccessId}`,
              payload,
              () => fetchOrders(),
              { maxBatchSize: 5, batchTimeoutMs: 400, priority: 'medium' }
            );
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    }
  }, [guestAccessId, hotelId]);

  return {
    services,
    orders,
    loading,
    createOrder,
    updateOrder,
    cancelOrder,
    refetch: () => Promise.all([fetchServices(), fetchOrders()])
  };
};